import type { ChatToolCatalogItem, ChatToolCategory, ChatToolSet } from '@pm-next/shared-types';

export type ComposerTriggerKind = 'tool' | 'set';

export interface ComposerTrigger {
  kind: ComposerTriggerKind;
  query: string;
  start: number;
  end: number;
}

export const TOOL_CATEGORY_LABELS: Record<ChatToolCategory, string> = {
  workforce: 'Workforce',
  payroll: 'Payroll',
  leave_attendance: 'Leave & attendance',
  compliance: 'Compliance',
  meta: 'Knowledge',
};

const CATEGORY_ORDER: ChatToolCategory[] = [
  'workforce',
  'payroll',
  'leave_attendance',
  'compliance',
  'meta',
];

export function detectComposerTrigger(value: string, cursor: number): ComposerTrigger | null {
  const before = value.slice(0, cursor);
  const match = /(^|[\s])([@/])([^\s]*)$/.exec(before);
  if (!match) return null;
  const query = match[3] ?? '';
  const start = before.length - query.length - 1;
  return {
    kind: match[2] === '@' ? 'tool' : 'set',
    query,
    start,
    end: cursor,
  };
}

export function insertComposerToken(
  value: string,
  trigger: ComposerTrigger,
  token: string
): { next: string; cursor: number } {
  const replacement = `${token} `;
  const next = value.slice(0, trigger.start) + replacement + value.slice(trigger.end);
  return { next, cursor: trigger.start + replacement.length };
}

function matchesQuery(haystacks: string[], query: string): boolean {
  const q = query.trim().toLowerCase().replace(/^[@/]/, '');
  if (!q) return true;
  return haystacks.some((text) => text.toLowerCase().includes(q));
}

export function filterComposerTools(
  tools: ChatToolCatalogItem[],
  query: string
): ChatToolCatalogItem[] {
  return tools.filter((tool) =>
    matchesQuery([tool.name, tool.label, tool.summary, TOOL_CATEGORY_LABELS[tool.category]], query)
  );
}

export function filterComposerToolSets(toolSets: ChatToolSet[], query: string): ChatToolSet[] {
  return toolSets.filter((set) =>
    matchesQuery([set.id, set.label, set.description, TOOL_CATEGORY_LABELS[set.category]], query)
  );
}

export function groupComposerTools(
  tools: ChatToolCatalogItem[]
): Array<{ category: ChatToolCategory; label: string; tools: ChatToolCatalogItem[] }> {
  return CATEGORY_ORDER.flatMap((category) => {
    const group = tools.filter((tool) => tool.category === category);
    if (group.length === 0) return [];
    return [{ category, label: TOOL_CATEGORY_LABELS[category], tools: group }];
  });
}
